import React from 'react';
import { 
  LayoutDashboard, 
  Tag, 
  ShoppingCart, 
  Package, 
  Users, 
  Wallet, 
  TrendingUp, 
  Settings, 
  HelpCircle,
  ShieldCheck,
  MessageSquare,
  Receipt
} from 'lucide-react';

export const MODULES = [
  { 
    id: 'dashboard', 
    label: 'Dashboard', 
    icon: <LayoutDashboard className="w-4 h-4" />,
    subModules: []
  },
  { 
    id: 'sales', 
    label: 'Sales', 
    icon: <Tag className="w-4 h-4" />,
    subModules: [
      { id: 'quotes', label: 'Quotes' },
      { id: 'invoices', label: 'Invoices' },
      { id: 'deliveries', label: 'Deliveries' }
    ]
  },
  { 
    id: 'pos', 
    label: 'POS', 
    icon: <Receipt className="w-4 h-4" />,
    subModules: [
      { id: 'pos_sell', label: 'Sell Screen' },
      { id: 'pos_history', label: 'Sales History' },
      { id: 'pos_catalog', label: 'Catalog' },
      { id: 'stores', label: 'Stores' }
    ]
  },
  { 
    id: 'purchases', 
    label: 'Purchases', 
    icon: <ShoppingCart className="w-4 h-4" />,
    subModules: [
      { id: 'purchase_orders', label: 'Purchase Orders' },
      { id: 'bills', label: 'Bills' },
      { id: 'purchase_deliveries', label: 'Deliveries' },
      { id: 'expense_submissions', label: 'Expense Submissions' },
      { id: 'landed_costs', label: 'Landed Costs' }
    ]
  },
  { 
    id: 'contacts', 
    label: 'Contacts', 
    icon: <Users className="w-4 h-4" />,
    subModules: [
      { id: 'contacts', label: 'All Contacts' },
      { id: 'customers', label: 'Customers' },
      { id: 'suppliers', label: 'Suppliers' }
    ]
  },
  { 
    id: 'inventory', 
    label: 'Inventory', 
    icon: <Package className="w-4 h-4" />,
    subModules: [
      { id: 'inventory_manager', label: 'Inventory Manager' },
      { id: 'inventory_reports', label: 'Inventory Reports' }
    ]
  },
  { 
    id: 'hr', 
    label: 'HR', 
    icon: <Users className="w-4 h-4" />,
    subModules: [
      { id: 'payrun', label: 'PayRun' },
      { id: 'employees', label: 'Employees' },
      { id: 'payrun_settings', label: 'Payrun Settings' },
      { id: 'attendance', label: 'Check In and Check Out' },
      { id: 'tasks_mops', label: 'Task/Activities' }
    ]
  },
  { 
    id: 'discussions', 
    label: 'Discussions', 
    icon: <MessageSquare className="w-4 h-4" />,
    subModules: []
  },
  { 
    id: 'reports', 
    label: 'Reports', 
    icon: <TrendingUp className="w-4 h-4" />,
    subModules: []
  },
  { 
    id: 'accounting', 
    label: 'Accounting', 
    icon: <Wallet className="w-4 h-4" />,
    subModules: [
      { id: 'accounting_dashboard', label: 'Dashboard' },
      { id: 'manual_journal', label: 'Manual Journal' },
      { id: 'trial_balance', label: 'Trial Balance' },
      { id: 'accounting_configuration', label: 'Configuration' }
    ]
  }
];

export const CORE_CHART_OF_ACCOUNTS = [
  { code: '1010', name: 'Cash on Hand', type: 'Bank', nature: 'Debit', enablePayments: true },
  { code: '1020', name: 'Bank - Operating Account', type: 'Bank', nature: 'Debit', enablePayments: true },
  { code: '1030', name: 'Bank - Savings Account', type: 'Bank', nature: 'Debit', enablePayments: true },
  { code: '1100', name: 'Accounts Receivable', type: 'Current Asset', nature: 'Debit' },
  { code: '1150', name: 'Customer Advances / Deposits Held', type: 'Current Liability', nature: 'Credit' },
  { code: '1200', name: 'Inventory / Stock on Hand', type: 'Current Asset', nature: 'Debit' },
  { code: '1210', name: 'Inventory Adjustments', type: 'Direct Cost', nature: 'Debit' },
  { code: '1300', name: 'Prepaid Expenses', type: 'Current Asset', nature: 'Debit' },
  { code: '1400', name: 'VAT Input / Recoverable VAT', type: 'Current Asset', nature: 'Debit' },
  { code: '1510', name: 'Furniture and Fixtures', type: 'Fixed Asset', nature: 'Debit' },
  { code: '1520', name: 'Office Equipment', type: 'Fixed Asset', nature: 'Debit' },
  { code: '1530', name: 'Vehicles', type: 'Fixed Asset', nature: 'Debit' },
  { code: '1590', name: 'Accumulated Depreciation', type: 'Fixed Asset', nature: 'Credit' },
  { code: '2010', name: 'Accounts Payable / Trade Creditors', type: 'Current Liability', nature: 'Credit' },
  { code: '2020', name: 'Accrued Expenses', type: 'Current Liability', nature: 'Credit' },
  { code: '2030', name: 'Payroll Payable', type: 'Current Liability', nature: 'Credit' },
  { code: '2100', name: 'VAT Output / VAT Payable', type: 'Current Liability', nature: 'Credit' },
  { code: '2110', name: 'VAT Control / Settlement', type: 'Current Liability', nature: 'Credit' },
  { code: '2200', name: 'Payroll Tax / Statutory Liabilities', type: 'Current Liability', nature: 'Credit' },
  { code: '3010', name: 'Share Capital / Owner Capital', type: 'Equity', nature: 'Credit' },
  { code: '3020', name: 'Owner Drawings / Dividends', type: 'Equity', nature: 'Debit' },
  { code: '3100', name: 'Retained Earnings', type: 'Equity', nature: 'Credit' },
  { code: '4010', name: 'Sales Revenue - Goods', type: 'Sales', nature: 'Credit' },
  { code: '4020', name: 'Sales Revenue - Services', type: 'Sales', nature: 'Credit' },
  { code: '4030', name: 'Sales Discounts', type: 'Sales', nature: 'Debit' },
  { code: '4040', name: 'Sales Returns and Allowances', type: 'Sales', nature: 'Debit' },
  { code: '5010', name: 'Cost of Goods Sold', type: 'Direct Cost', nature: 'Debit' },
  { code: '5020', name: 'Purchases', type: 'Direct Cost', nature: 'Debit' },
  { code: '5030', name: 'Purchase Returns', type: 'Direct Cost', nature: 'Credit' },
  { code: '6010', name: 'Salaries and Wages', type: 'Expense', nature: 'Debit' },
  { code: '6030', name: 'Rent Expense', type: 'Expense', nature: 'Debit' },
  { code: '6040', name: 'Utilities', type: 'Expense', nature: 'Debit' },
  { code: '6050', name: 'Depreciation Expense', type: 'Expense', nature: 'Debit' },
  { code: '6110', name: 'Travel Expense', type: 'Expense', nature: 'Debit' },
  { code: '6120', name: 'Bank Charges', type: 'Expense', nature: 'Debit' },
  { code: '6140', name: 'Purchase Discounts / Income', type: 'Other Income', nature: 'Credit' },
  { code: '8990', name: 'Rounding Differences', type: 'Expense', nature: 'Debit' }
];

export const COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan",
  "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi",
  "Cabo Verde", "Cambodia", "Cameroon", "Canada", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic",
  "Denmark", "Djibouti", "Dominica", "Dominican Republic",
  "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia",
  "Fiji", "Finland", "France",
  "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana",
  "Haiti", "Honduras", "Hungary",
  "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy",
  "Jamaica", "Japan", "Jordan",
  "Kazakhstan", "Kenya", "Kiribati", "Kuwait", "Kyrgyzstan",
  "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg",
  "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar",
  "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia", "Norway",
  "Oman",
  "Pakistan", "Palau", "Palestine State", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal",
  "Qatar",
  "Romania", "Russia", "Rwanda",
  "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Korea", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria",
  "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu",
  "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States of America", "Uruguay", "Uzbekistan",
  "Vanuatu", "Venezuela", "Vietnam",
  "Yemen",
  "Zambia", "Zimbabwe"
];

export const CITY_MAP: { [key: string]: string[] } = {
  "Egypt": ["Cairo", "Giza", "Alexandria", "Luxor", "Aswan", "Sharm El Sheikh", "Hurghada", "Mansoura", "Tanta", "Port Said", "Suez", "Ismailia", "Fayoum", "Zagazig", "Asyut", "Damietta", "Minya", "Beni Suef", "Sohag", "Qena"],
  "Saudi Arabia": ["Riyadh", "Jeddah", "Mecca", "Medina", "Dammam", "Khobar", "Abha", "Tabuk", "Buraidah", "Hail"],
  "United Arab Emirates": ["Dubai", "Abu Dhabi", "Sharjah", "Ajman", "Ras Al Khaimah", "Fujairah", "Umm Al Quwain"],
  "United States of America": ["New York", "Los Angeles", "Chicago", "Houston", "Phoenix", "Philadelphia", "San Antonio", "San Diego", "Dallas", "San Jose"],
  "United Kingdom": ["London", "Birmingham", "Manchester", "Glasgow", "Liverpool", "Leeds", "Sheffield", "Edinburgh", "Bristol", "Leicester"],
  "France": ["Paris", "Lyon", "Marseille", "Toulouse", "Nice", "Nantes", "Strasbourg", "Montpellier", "Bordeaux", "Lille"],
  "Germany": ["Berlin", "Hamburg", "Munich", "Cologne", "Frankfurt", "Stuttgart", "Düsseldorf", "Dortmund", "Essen", "Leipzig"],
  "Jordan": ["Amman", "Zarqa", "Irbid", "Russeifa", "Sahab", "Aqaba", "Madaba", "Salt"],
  "Kuwait": ["Kuwait City", "Al Ahmadi", "Hawalli", "Salmiya", "Sabah Al Salem", "Al Farwaniyah"],
  "Qatar": ["Doha", "Al Rayyan", "Al Wakrah", "Al Khor", "Umm Salal", "Madinat ash Shamal"],
};
